import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchVideoProject } from '@/api';
import { SceneEditor, Scene } from '@/components/SceneEditor';

export default function EditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<any>(null);
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadProject = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await fetchVideoProject(id);
        setProject(data);

        // Parse scenes from assets
        if (data.assets) {
          const assets = typeof data.assets === 'string' ? JSON.parse(data.assets) : data.assets;
          setScenes(assets.scenes || []);
        }
      } catch (err) {
        console.error('Failed to load project:', err);
        setError('Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [id]);

  const handleScenesChange = (updatedScenes: Scene[]) => {
    setScenes(updatedScenes);
  };

  const handleSave = async () => {
    if (!project || !id) return;

    try {
      setIsSaving(true);
      
      // Update project with new scenes
      const response = await fetch(`/api/video-projects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          assets: {
            ...(project.assets || {}),
            scenes
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save changes');
      }

      const result = await response.json();
      setProject(result.data);
      setError(null);
    } catch (err) {
      console.error('Failed to save:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center animate-pulse">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-600">Loading editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate(`/projects/${id}`)}
              className="text-indigo-600 hover:text-indigo-700 text-sm font-medium mb-2"
            >
              ← Back to Project
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{project?.title}</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Professional Scene Editor</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/projects/${id}/storyboard`)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              View Storyboard
            </button>
            <button
              onClick={() => navigate(`/projects/${id}`)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
            >
              Next: Animations
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-6 py-4 mt-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
            {error}
          </div>
        </div>
      )}

      {/* Editor */}
      <div className="max-w-7xl mx-auto">
        {scenes.length > 0 ? (
          <SceneEditor
            scenes={scenes}
            onScenesChange={handleScenesChange}
            onSave={handleSave}
            isSaving={isSaving}
          />
        ) : (
          <div className="p-12 text-center">
            <div className="text-4xl mb-4">🎬</div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Scenes Found</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Generate a script first to create scenes for editing
            </p>
            <button
              onClick={() => navigate(`/projects/${id}/storyboard`)}
              className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
            >
              Go to Storyboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
